import React, { useState, useCallback, useContext } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppStoreContext } from '../../context/appStoreContext';
import { useSessionContext } from '../../context/sessionContext';
import { SUPPORT_EMAIL_ID } from '../../utils/helper';
import { fetchPlanUsageDetails } from '../../utils/services/planUsage';
import CreateTemplateModal from '../../components/CreateNewTemplate/CreateTemplateModal';
import CreateFlowModal from '../../components/CreateNewTemplate/CreateFlowModal';
import CreateBlockModal from '../../components/CreateNewTemplate/CreateBlockModal';
import SetupErrorModal from '../../components/Modals/SetupErrorModal';
import UpgradePlanModal from '../../components/Modals/UpgradePlanModal';
import { CREATE_NEW_TEMPLATE_MODAL } from '../../components/CreateNewTemplate/translationValues';
import styled from 'styled-components';
import { Icon } from 'antd';
import { NEW_TEMPLATE_SECTION } from './translationValues';

const NAMESPACES = ['CREATE_NEW_TEMPLATE_MODAL', 'NEW_TEMPLATE_SECTION'];

const TopBarContainer = styled.div`
  border-bottom: 1px solid rgba(229, 231, 235, 0.8);
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(40px);
`;

const TopSection = styled.div`
  padding: ${props => (props.sidebarCollapsed ? '32px 80px 24px' : '32px 32px 24px')};
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  transition: padding 0.3s ease;
`;

const Title = styled.h1`
  margin-bottom: 8px;
  background: linear-gradient(to right, #111827, #4b5563);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-size: 40px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
`;

const BottomSection = styled.div`
  padding: ${props => (props.sidebarCollapsed ? '16px 80px 24px' : '16px 32px 24px')};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  transition: padding 0.3s ease;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const ButtonWrapper = styled.div`
  position: relative;
  &:hover .plus-button {
    opacity: 1;
  }
`;

const PlusButton = styled.button`
  position: absolute;
  top: -32px;
  left: 50%;
  transform: translateX(-50%);
  border: none;
  border-radius: 50%;
  height: 28px;
  width: 28px;
  background: #1f2937;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  z-index: 10;
  transition: all 0.2s;
  cursor: pointer;
  &:hover {
    transform: translateX(-50%) scale(1.1);
  }
  svg {
    color: white;
  }
`;

const ViewButton = styled.button`
  border: ${props => (props.active ? 'none' : '1px solid #e5e7eb')};
  border-radius: 16px;
  padding: 10px 20px;
  background: ${props => props.active ? 'linear-gradient(to right, rgb(107, 141, 214), rgb(142, 55, 215))'
      : 'linear-gradient(to right, rgb(239, 157, 67), rgb(231, 85, 22))'};      
  color: white;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
`;

const TopBar = ({ selectedView, onViewChange, sidebarCollapsed }) => {
  const { t } = useTranslation(NAMESPACES);

  const titleMap = {
    documents: {
      title: t(NEW_TEMPLATE_SECTION.DOCUMENTS.key, NEW_TEMPLATE_SECTION.DOCUMENTS.value),
      description: t(
        NEW_TEMPLATE_SECTION.DOCUMENTS_DESC.key,
        NEW_TEMPLATE_SECTION.DOCUMENTS_DESC.value,
      ),
    },
    reusable_pages: {
      title: t(NEW_TEMPLATE_SECTION.REUSABLE_PAGES.key, NEW_TEMPLATE_SECTION.REUSABLE_PAGES.value),
      description: t(
        NEW_TEMPLATE_SECTION.REUSABLE_PAGES_DESC.key,
        NEW_TEMPLATE_SECTION.REUSABLE_PAGES_DESC.value,
      ),
    },
    reusable_sections: {
      title: t(
        NEW_TEMPLATE_SECTION.REUSABLE_SECTIONS.key,
        NEW_TEMPLATE_SECTION.REUSABLE_SECTIONS.value,
      ),
      description: t(
        NEW_TEMPLATE_SECTION.REUSABLE_SECTIONS_DESC.key,
        NEW_TEMPLATE_SECTION.REUSABLE_SECTIONS_DESC.value,
      ),
    },
  };

  const [showModal, setShowModal] = useState(null); // documents | reusable_pages | reusable_sections
  const [isCheckingLimit, setIsCheckingLimit] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [planName, setPlanName] = useState('');
  const [documentLimit, setDocumentLimit] = useState(null);
  const [showSetupFailureError, setShowSetupFailureError] = useState(false);

  const history = useHistory();
  const location = useLocation();

  const { isEnterprise, checkUserAccess, checkUserAccessRoles } = useContext(AppStoreContext);

  const organisationSelector = useCallback(store => store.organizationSetup, []);
  const [organizationSetup] = useSessionContext(organisationSelector);

  const isEmailContent = location.pathname === '/emailTemplates';
  const isSaasSetupFailed =
    !isEnterprise && checkUserAccess('client') && organizationSetup !== 'completed';

  const redirectToTemplate = (template, ...rest) => {
    const isComplex = rest[0] === 'isComplex';
    if (isComplex) {
      history.push(`/design/${template?.documentId}`);
    } else {
      if (template.id) {
        history.push(`/design/${template.id}`);
      }
    }
  };
  const redirectToFlow = id => {
    history.push(`/flow/${id}`);
  };
  const redirectToBlock = id => {
    history.push(`/block/${id}`);
  };

  const handleCreateDocTemplate = async () => {
    if (isSaasSetupFailed) {
      return setShowSetupFailureError(true);
    }
    if (isEnterprise || checkUserAccess('master')) {
      setShowModal('documents');
      return;
    }
    try {
      setIsCheckingLimit(true);
      const response = await fetchPlanUsageDetails();
      if (response?.statusCode === 1918) {
        const { templateLimit: created } = response.data.usage;
        const { templateLimit: limit, planName } = response.data.upperLimit;
        if (created >= limit) {
          setDocumentLimit(limit);
          setPlanName(planName);
          setShowUpgradeModal(true);
        } else {
          setShowModal('documents');
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsCheckingLimit(false);
    }
  };

  return (
    <TopBarContainer>
      <TopSection sidebarCollapsed={sidebarCollapsed}>
        <div>
          <Title>{titleMap[selectedView].title}</Title>
          <Subtitle>{titleMap[selectedView].description}</Subtitle>
        </div>
      </TopSection>

      <BottomSection sidebarCollapsed={sidebarCollapsed}>
        <ButtonGroup>
          <ButtonWrapper className="group">
            <PlusButton className="plus-button">
              {isCheckingLimit ? (
                <Icon type="loading" />
              ) : (
                <Icon
                  type="plus"
                  style={{ fontSize: 14 }}
                  onClick={() => {
                    if (!isEmailContent) {
                      handleCreateDocTemplate();
                    } else {
                      setShowModal('documents');
                    }
                  }}
                />
              )}
            </PlusButton>
            <ViewButton
              active={selectedView === 'documents'}
              view="documents"
              onClick={() => onViewChange('documents')}
            >
              {t(NEW_TEMPLATE_SECTION.DOCUMENTS.key, NEW_TEMPLATE_SECTION.DOCUMENTS.value)}
            </ViewButton>
          </ButtonWrapper>

          <ButtonWrapper className="group">
            {checkUserAccessRoles(['REUSABLECONTENTMANAGER']) && (
              <PlusButton className="plus-button">
                <Icon
                  type="plus"
                  style={{ fontSize: 14 }}
                  onClick={() => {
                    if (isSaasSetupFailed) setShowSetupFailureError(true);
                    else setShowModal('reusable_pages');
                  }}
                />
              </PlusButton>
            )}

            <ViewButton
              active={selectedView === 'reusable_pages'}
              view="reusable_pages"
              onClick={() => onViewChange('reusable_pages')}
            >
              {t(NEW_TEMPLATE_SECTION.REUSABLE_PAGES.key, NEW_TEMPLATE_SECTION.REUSABLE_PAGES.value)}
            </ViewButton>
          </ButtonWrapper>

          <ButtonWrapper className="group">
            {checkUserAccessRoles(['REUSABLECONTENTMANAGER']) && (
              <PlusButton className="plus-button">
                <Icon
                  type="plus"
                  style={{ fontSize: 14 }}
                  onClick={() => {
                    if (isSaasSetupFailed) setShowSetupFailureError(true);
                    else setShowModal('reusable_sections');
                  }}
                />
              </PlusButton>
            )}
            <ViewButton
              active={selectedView === 'reusable_sections'}
              view="reusable_sections"
              onClick={() => onViewChange('reusable_sections')}
            >
              {t(
                NEW_TEMPLATE_SECTION.REUSABLE_SECTIONS.key,
                NEW_TEMPLATE_SECTION.REUSABLE_SECTIONS.value,
              )}
            </ViewButton>
          </ButtonWrapper>
        </ButtonGroup>
      </BottomSection>

      {showModal === 'documents' && (
        <CreateTemplateModal
          visible={true}
          onCancel={() => setShowModal(null)}
          onSucess={redirectToTemplate}
        />
      )}
      {showModal === 'reusable_pages' && (
        <CreateFlowModal onCancel={() => setShowModal(null)} onSucess={redirectToFlow} />
      )}
      {showModal === 'reusable_sections' && (
        <CreateBlockModal onCancel={() => setShowModal(null)} onSucess={redirectToBlock} />
      )}

      <UpgradePlanModal
        visible={showUpgradeModal}
        modalText={
          planName === 'Premium'
            ? t(
              CREATE_NEW_TEMPLATE_MODAL.PREMIUM_DOCUMENT_CREATION_LIMIT_EXCEEDED.key,
              CREATE_NEW_TEMPLATE_MODAL.PREMIUM_DOCUMENT_CREATION_LIMIT_EXCEEDED.value,
              { emailId: SUPPORT_EMAIL_ID },
            )
            : planName === 'Free'
              ? t(
                CREATE_NEW_TEMPLATE_MODAL.FREE_PLAN_UPGRADE_MESSAGE.key,
                CREATE_NEW_TEMPLATE_MODAL.FREE_PLAN_UPGRADE_MESSAGE.value,
                { documentLimit },
              )
              : t(
                CREATE_NEW_TEMPLATE_MODAL.STANDARD_PLAN_UPGRADE_MESSAGE.key,
                CREATE_NEW_TEMPLATE_MODAL.STANDARD_PLAN_UPGRADE_MESSAGE.value,
                { documentLimit },
              )
        }
        onCancel={() => setShowUpgradeModal(false)}
      />

      <SetupErrorModal showError={showSetupFailureError} setShowError={setShowSetupFailureError} />
    </TopBarContainer>
  );
};

export default TopBar;
